import React, { useState, useEffect, useCallback } from "react";
import { Sparkles, RefreshCw, Cpu, BarChart3 } from "lucide-react";
import * as api from "@/src/api/sdk.gen";

export interface IntelligencePageProps {
  slug: string;
  onNavigateToAssignments?: () => void;
}

export const IntelligencePage: React.FC<IntelligencePageProps> = ({
  slug,
  onNavigateToAssignments,
}) => {
  const [intelligence, setIntelligence] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);

  const loadIntelligence = useCallback(async () => {
    if (!slug) return;
    try {
      setLoading(true);
      const { data } = await api.getSkillIntelligenceAndInterventionsApiV1B2bSlugSkillIntelligenceGet({
        path: { slug },
      });
      if (data) {
        setIntelligence(data as any);
      }
    } catch (err) {
      console.error("Failed to load skill intelligence:", err);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void loadIntelligence();
  }, [loadIntelligence]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-3xl shadow-xs">
        <div>
          <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Skill Intelligence &amp; AI-Driven Interventions
          </h3>
          <p className="text-xs text-slate-500 max-w-xl">
            Real-time aggregation of employee competency gaps and diagnostic scores across departments, generating automated training recommendations (Node N9).
          </p>
        </div>
        <button
          onClick={loadIntelligence}
          disabled={loading}
          className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-xl flex items-center gap-1.5 border border-slate-200 shadow-xs transition disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-purple-600" : ""}`} />
          Refresh Intelligence
        </button>
      </div>

      {/* AI Interventions Recommendations */}
      <div className="space-y-4">
        <h4 className="text-xs font-bold uppercase tracking-wider text-purple-700 flex items-center gap-2">
          <Cpu className="w-4 h-4 text-purple-600" />
          Recommended AI Interventions ({intelligence?.interventions?.length || 0})
        </h4>

        {intelligence?.interventions?.length === 0 ? (
          <div className="p-8 bg-white border border-slate-200 rounded-2xl text-center text-xs text-slate-500 shadow-xs">
            No critical competency gaps detected. Your team is currently meeting benchmark targets!
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {intelligence?.interventions?.map((item: any, idx: number) => (
              <div
                key={idx}
                className="p-5 bg-white border border-purple-100 rounded-2xl flex flex-col justify-between shadow-xs hover:border-purple-200 transition"
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="px-2.5 py-0.5 bg-purple-50 border border-purple-200 text-purple-700 text-[10px] font-bold uppercase rounded-md">
                      {item.department} • {item.skill_name}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${
                        item.impact_level === "High"
                          ? "bg-rose-50 border border-rose-200 text-rose-700"
                          : "bg-amber-50 border border-amber-200 text-amber-700"
                      }`}
                    >
                      {item.gap_percentage}% Gap Detected
                    </span>
                  </div>
                  <p className="text-xs text-slate-700 mt-2 leading-relaxed">{item.recommended_action}</p>
                </div>

                {item.suggested_course_title && (
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-[11px] text-slate-600 truncate">
                      Track: <strong className="text-slate-800">{item.suggested_course_title}</strong>
                    </span>
                    {onNavigateToAssignments && (
                      <button
                        onClick={onNavigateToAssignments}
                        className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 shrink-0"
                      >
                        Assign Rule →
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Department Skill Gap Breakdown Matrix */}
      <div className="space-y-4 pt-4">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-indigo-600" />
          Department Skill Breakdown
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(intelligence?.department_skill_matrix || {}).map(([dept, data]: [string, any]) => (
            <div key={dept} className="p-5 bg-white border border-slate-200 rounded-2xl shadow-xs">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-bold text-slate-900">{dept}</span>
                <span className="text-xs text-slate-500">{data.total_assessed} Assessed</span>
              </div>
              <div className="space-y-2">
                {Object.entries(data.skills || {}).map(([skill, sData]: [string, any]) => (
                  <div
                    key={skill}
                    className="flex items-center justify-between text-xs py-1.5 border-b border-slate-100 last:border-0"
                  >
                    <span className="text-slate-700 font-medium">{skill}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-mono text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">
                        {sData.gaps} gaps
                      </span>
                      <span className="text-[11px] font-bold text-emerald-600">
                        {sData.total ? Math.round(sData.avg_score / sData.total) : 0}% avg
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
